import type { ChangeEvent, UIEvent } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useGetListsInfinite, usePatchDepartment, useGetDepartment,
} from '@vlabs/api/artemy/client';
import type { ListId } from '@vlabs/api/artemy/model';
import { CheckIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useAttachDepartmentListsTranslation } from '../i18n';
import type { AttachDepartmentListsProps } from '../types';

const POPUP_WIDTH = 280;
const POPUP_MAX_HEIGHT = 360;
const SCROLL_THRESHOLD = 8;
const PAGE_SIZE = 10;

const displayName = 'AttachDepartmentListsDropdown';
const testid = buildTestid(displayName);

interface AttachDepartmentListsDropdownProps extends AttachDepartmentListsProps {
  children: React.ReactElement;
  disabled?: boolean;
}

function AttachDepartmentListsDropdown({
  children,
  departmentId,
  disabled = false,
}: AttachDepartmentListsDropdownProps): React.JSX.Element {
  const { t } = useAttachDepartmentListsTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');

  const getDepartment = useGetDepartment(departmentId, {
    query: { enabled: isOpen },
  });
  const departmentLists = getDepartment.data?.lists ?? [];
  const departmentListIds = new Set(
    departmentLists.map(({ list_id }) => list_id),
  );

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => {
      setQuery(value);
    }, 300),
    [],
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const getLists = useGetListsInfinite(
    {
      page: 1,
      page_size: PAGE_SIZE,
      user_data: query || undefined,
    },
    {
      query: {
        enabled: isOpen,
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => (
          (lastPage?.lists?.length ?? 0) < PAGE_SIZE ? undefined : allPages.length + 1
        ),
      },
    },
  );

  const patchDepartment = usePatchDepartment({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        setIsOpen(false);
      },
      onError: () => {
        toast.error(t('on_error'));
      },
    },
  });

  const lists = useMemo(() => {
    if (!getLists.data?.pages) return [];
    return getLists.data.pages.flatMap((page) => page?.lists ?? []);
  }, [getLists.data]);

  const isLoading = isOpen && (getLists.isLoading || getDepartment.isFetching);
  const hasError = getLists.isError || getDepartment.isError;
  const isDisabled = disabled || patchDepartment.isPending;

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      debouncedSetQuery.cancel();
      setInputValue('');
      setQuery('');
    }
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
    debouncedSetQuery(e.target.value);
  };

  const handleAttachList = (listId: ListId | undefined): void => {
    if (!listId || !getDepartment.isSuccess) return;

    patchDepartment.mutate({
      departmentId,
      data: {
        lists: [
          ...departmentLists,
          { list_id: listId, open: false },
        ],
      },
    });
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD;
    if (isNearBottom && getLists.hasNextPage && !getLists.isFetchingNextPage) {
      getLists.fetchNextPage();
    }
  };

  return (
    <DropdownMenu.Root
      modal={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <DropdownMenu.Trigger
        disabled={isDisabled}
        render={children}
      />
      <DropdownMenu.Portal>
        <DropdownMenu.Positioner>
          <DropdownMenu.Popup
            onScroll={handleScroll}
            style={{
              maxHeight: POPUP_MAX_HEIGHT,
              overflowY: 'auto',
              width: POPUP_WIDTH,
            }}
          >
            <DropdownMenu.SearchItem
              onChange={handleQueryChange}
              placeholder={t('search_placeholder')}
              size="small"
              value={inputValue}
            />
            {!!isLoading && (
              <DropdownMenu.EmptyItem {...testid('loading')}>{t('loading')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !!hasError && (
              <DropdownMenu.EmptyItem {...testid('load-error')}>{t('load_error')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !hasError && lists.length === 0 && (
              <DropdownMenu.EmptyItem {...testid('empty')}>{t('empty')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !hasError && lists.map(({ user_data, list_id }) => {
              const isAttached = departmentListIds.has(list_id);

              return (
                <DropdownMenu.Item
                  {...testid(list_id)}
                  decorationRight={!!isAttached && <CheckIcon />}
                  disabled={patchDepartment.isPending || isAttached}
                  key={list_id}
                  onClick={() => handleAttachList(list_id)}
                >
                  {user_data ?? list_id}
                </DropdownMenu.Item>
              );
            })}
            {!isLoading && !hasError && !!getLists.isFetchingNextPage && (
              <DropdownMenu.EmptyItem {...testid('loading-more')}>{t('loading')}</DropdownMenu.EmptyItem>
            )}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

AttachDepartmentListsDropdown.displayName = displayName;

export { AttachDepartmentListsDropdown };
